import React, { useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Firebase from "../../../services/firebase";
import { FormContainer, Row, StyledButton, StyledForm, StyledInput, StyledLabel, StyledLeftForm, StyledRightForm, StyledStar } from "../signIn/signin.style";
import { StyledFormWrapper, StyledInputGroupRow, StyledSignUp } from "./signup.style";
import Recommendation from "../../commons/recommendation/Recommendation.component";
import { createCustomer, createPlan, getPlansByCustomerId, getPlansByMail } from "../../../services/globalService";
import { ActionRow, ErrorContainer, StyledTitle } from "../../style/global.style";
import StateContext from "../../../context/StateContext";
import SmallLoader from "../../utils/SmallLoader";
import { sendEvent } from "../../../services/analytics";
import { prepPlansMap } from "../../../services/dataService";
import { captureException } from "services/sentry";
import {ReactComponent as Logo} from "../../../img/Fixel_Logo2019vector1.svg";

const SignUp = () => {
	const { dispatch, ACTIONS } = useContext(StateContext);
	const history = useHistory();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [error, setError] = useState(null);
	const [showSpinner, setShowSpinner] = useState(false);

	const createUserWithEmailAndPasswordHandler = async (event) => {
		event.preventDefault();
		setShowSpinner(true);
		try {
			let displayName = `${firstName} ${lastName}`;
			const newUser = await Firebase.register(displayName, email, password, onSignupFail);
			registerToFixel(newUser, "user-pass");
		} catch (e) {
			onSignupFail(e.message);
		}
	};

	const onSignupFail = (e) => {
		setShowSpinner(false);
		setError(e.message || e);
	};

	const createNewPlan = async (newCust) => {
		try {
			const exPlanRes = await getPlansByMail({ mail: newCust.mail });
			return exPlanRes?.data;
		} catch (error) {
			if (error.status === 404) {
				try {
					const plansByCustId = await getPlansByCustomerId({ cust_id: newCust.cust_no });
					return plansByCustId?.data;
				} catch (error2) {
					if (error2.status === 404) {
						const newPlanRes = await createPlan({ cust_id: newCust.cust_no, type: "Trial", name: "console", mail: newCust.mail });
						return newPlanRes.data;
					} else throw error2;
				}
			} else throw error;
		}
	};
	/**
	 * Register firebase user to a fixel customer
	 * @returns {Promise<void>}
	 */
	const registerToFixel = async (newUser, signInType) => {
		const payload = { user: newUser };
		// fixel api get/create customer
		try {
			const { data, status } = (await createCustomer({ name: newUser.displayName, mail: newUser.email })) || {};
			payload.customer = data;
			payload.haveCustomer = true;
			const newPlan = await createNewPlan(data);
			if (newPlan && (!Array.isArray(newPlan) || newPlan.length)) {
				setShowSpinner(false);
				sendEvent({ category: "sign up", action: signInType });
				payload.plans = Array.isArray(newPlan) ? newPlan : [newPlan];
				payload.plansMap = prepPlansMap(Array.isArray(newPlan) ? newPlan : [newPlan]);
				payload.havePlans = true;
				payload.showSpinner = false;
				dispatch({ type: ACTIONS.SET_STATE, payload });
				// if a new plan was created (status 201) redirect to create segment
				if (status === 201) return history.push("/introduction");
				// else = cust had plans (status 200) - redirect as an existing customer
				else return history.push("/");
			} else throw new Error("error in creating new plan");
		} catch (e) {
			console.error("SignUp.component.js ~ registerToFixel ~ e", e.message || e);
			// if creating a customer and a plan wasn't successful delete firebase user
			setError("There was a problem creating your account. Please try again later or contact us at support@fixel.ai");
			const didDelete = Firebase.deleteUser();
			setShowSpinner(false);
			if (!didDelete) history.push({ pathname: "/error-page", state: e });
			captureException(e, "error", {email: newUser?.email || 'unknown', username: newUser?.displayName || 'unknown'}, { action: "registerToFixel" }, { scope: "signUp.component.js" });
		}
	};

	const onChangeHandler = (event) => {
		const { name, value } = event.currentTarget;
		switch (name) {
			case "userEmail":
				setEmail(value);
				break;
			case "userPassword":
				setPassword(value);
				break;
			case "firstName":
				setFirstName(value);
				break;
			case "lastName":
				setLastName(value);
				break;
			default:
				break;
		}
	};

	return (
		<StyledSignUp>
			<Logo style={{"margin": "2rem 0"}}/>
			<StyledFormWrapper>
				<StyledLeftForm>
					{error && <ErrorContainer className={"shake"}>{error}</ErrorContainer>}
					{!error && <div />}
					<FormContainer>
						<StyledTitle>Create Your Account</StyledTitle>
						<StyledForm onSubmit={createUserWithEmailAndPasswordHandler}>
							<StyledInputGroupRow>
								<div>
									<StyledLabel htmlFor="firstName">
										First Name
										<StyledStar>*</StyledStar>
									</StyledLabel>
									<StyledInput required disabled={showSpinner} type="text" name="firstName" value={firstName} placeholder="Jim" id="firstName" onChange={onChangeHandler} />
								</div>
								<div>
									<StyledLabel htmlFor="lastName">
										Last Name
										<StyledStar>*</StyledStar>
									</StyledLabel>
									<StyledInput required disabled={showSpinner} type="text" name="lastName" value={lastName} placeholder="Henson" id="lastName" onChange={onChangeHandler} />
								</div>
							</StyledInputGroupRow>
							<StyledLabel htmlFor="userEmail">
								Email
								<StyledStar>*</StyledStar>
							</StyledLabel>
							<StyledInput
								required
								disabled={showSpinner}
								type="email"
								name="userEmail"
								value={email}
								placeholder="name@example.com"
								id="userEmail"
								autocomplete="email"
								onChange={onChangeHandler}
							/>
							<StyledLabel htmlFor="userPassword">
								Password
								<StyledStar>*</StyledStar>
							</StyledLabel>
							<StyledInput
								required
								disabled={showSpinner}
								type="password"
								name="userPassword"
								value={password}
								placeholder="at least 6 characters"
								id="userPassword"
								autocomplete="current-password"
								onChange={onChangeHandler}
							/>
							<ActionRow>
								<StyledLabel htmlFor="consent">
									By signing up to Fixel you agree to the{" "}
									<a target="_blank" rel="noreferrer" href="https://fixel.ai/privacy-policy/">
										terms of use and the privacy policy
									</a>
									<StyledStar>*</StyledStar>
								</StyledLabel>
							</ActionRow>
							<Row className={"mt-1"}>
								<StyledButton type="submit" disabled={showSpinner}>
									{" "}
									{showSpinner ? <SmallLoader inline={true} /> : "Get Started"}
								</StyledButton>
							</Row>
						</StyledForm>
						<ActionRow>
							<p>Do you have an account already?</p>
							<Link disabled={showSpinner} to="/login">
								Log in
							</Link>
						</ActionRow>
					</FormContainer>
				</StyledLeftForm>
				<StyledRightForm>
					<Recommendation />
				</StyledRightForm>
			</StyledFormWrapper>
		</StyledSignUp>
	);
};
export default React.memo(SignUp);
